/**
 * PaymentPageV2 - Main entry point for the new payment page
 *
 * Features:
 * - Wraps with Stripe Elements provider
 * - Two-column responsive layout (desktop)
 * - Single column with correct ordering (mobile)
 * - Sticky CTA button on mobile only
 */

import { Elements } from '@stripe/react-stripe-js';
import { useSearchParams } from 'react-router-dom';
import usePaymentController, { useStripeLoadStatus } from '@/app/components/payment/payment.controller';
import { StripeLoadError } from '@/app/components/payment/StripeLoadError';
import useCollectPaymentMethod from '@/app/components/payment/components/method/payment-method-v2.controller';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { useFeatureFlags, VariantFlags } from '@/services/feature-flag-provider';
import {
  StripeAmountProvider,
  useStripeAmount,
} from '@/app/components/ppra/components/ppra-payment-page/contexts/StripeAmountContext';
import { useStripeAmountUpdater } from '@/app/components/ppra/components/ppra-payment-page/hooks/useStripeAmountUpdater';

import { PaymentPageLayout } from './PaymentPageLayout';
import { Header } from './components/Header';
import { TestimonialsSection } from './components/left-column/TestimonialsSection';
import { PrivacySection } from './components/left-column/PrivacySection';
import { FAQSection } from './components/FAQSection';
import { PaymentCard } from './components/right-column/PaymentCard';
import { FooterLinks } from './components/FooterLinks';
import { useZipPriceRefetch } from './useZipPriceRefetch';
import { FooterDisclaimer } from './components/FooterDisclaimer';
import { ContactButton } from './components/ContactButton';
import { StickyPricingCTA } from './components/StickyPricingCTA';

import {
  PricingHeader,
  EmailField,
  NameField,
  CardSection,
  ExpressCheckoutSection,
  ZipCodeField,
  CouponInput,
  TrialChargeNotice,
  PaymentTrustBadges,
  LegalDisclaimer,
  HsaFsaDisclaimer,
} from '@/components/payment-form';
import { useEnsureFunnelSession, useFunnelLead } from '@/hooks/useFunnelLead';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { usePurchaseModel } from '@/hooks/use-purchase-model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COHORT_STORAGE_KEY, POSITIONING_VARIANT_STORAGE_KEY, POSITIONING_VARIANTS, type PositioningVariant } from '@/constants/payment';

/**
 * Get cohort from URL param or sessionStorage (for redirect preservation).
 * URL param takes precedence; if found, it's stored for later retrieval.
 * Defaults to 'cancer' if no cohort is specified.
 */
function getCohort(searchParams: URLSearchParams): string {
  const urlCohort = searchParams.get('cohort');

  if (urlCohort) {
    // Store in sessionStorage for redirect preservation
    sessionStorage.setItem(COHORT_STORAGE_KEY, urlCohort);
    return urlCohort;
  }

  // Check sessionStorage (after 3DS redirect)
  const storedCohort = sessionStorage.getItem(COHORT_STORAGE_KEY);
  if (storedCohort) {
    return storedCohort;
  }

  // Default to cancer cohort and persist for redirect preservation
  sessionStorage.setItem(COHORT_STORAGE_KEY, 'cancer');
  return 'cancer';
}

/**
 * Resolve positioning variant from `?positioning=` URL param, falling back to
 * sessionStorage (for 3DS/Stripe redirect preservation), then 'default'.
 * Mirrors the pattern used for cohort.
 */
function getPositioningVariant(searchParams: URLSearchParams): PositioningVariant {
  const param = searchParams.get('positioning');

  const isValid = (v: string | null): v is PositioningVariant =>
    v !== null && (POSITIONING_VARIANTS as readonly string[]).includes(v);

  if (isValid(param)) {
    sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, param);
    return param;
  }

  const stored = sessionStorage.getItem(POSITIONING_VARIANT_STORAGE_KEY);
  if (isValid(stored)) {
    return stored;
  }

  sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, 'default');
  return 'default';
}

/**
 * PaymentForm - The actual payment form with all fields
 */
function PaymentForm() {
  useStripeAmountUpdater();
  const [searchParams] = useSearchParams();

  // Detect multi-disease cohort from URL params or sessionStorage
  const cohort = getCohort(searchParams);
  const isMultiDiseaseCohort = cohort === 'multi-disease';

  // Resolve positioning variant from URL param / sessionStorage / default
  const positioningVariant = getPositioningVariant(searchParams);

  // Set Amplitude user properties for cohort + positioning variant
  const { setMultiVerticalExperimentProperty, setPositioningVariantProperty, setPriceVariantProperty } =
    useAnalyticsCalls();
  const hasSetCohortRef = useRef(false);
  const hasSetPositioningRef = useRef(false);
  useEffect(() => {
    if (isMultiDiseaseCohort && !hasSetCohortRef.current) {
      hasSetCohortRef.current = true;
      setMultiVerticalExperimentProperty();
    }
  }, [isMultiDiseaseCohort, setMultiVerticalExperimentProperty]);
  useEffect(() => {
    if (!hasSetPositioningRef.current) {
      hasSetPositioningRef.current = true;
      setPositioningVariantProperty(positioningVariant);
    }
  }, [positioningVariant, setPositioningVariantProperty]);

  // Price experiment: set `price_variant` user property from the Amplitude
  // payload so events on /payment segment by the price the user actually
  // saw — covers users who deep-link to /payment without going through
  // FunnelGuard (email links, refreshes, bookmarks). Same Amplitude device_id
  // means the same bucket regardless of entry point. See
  // docs/specs/price-test-99-vs-199.md.
  const { getVariantPayload, isLoading: isFlagsLoading } = useFeatureFlags();
  const hasSetPriceRef = useRef(false);
  useEffect(() => {
    if (hasSetPriceRef.current || isFlagsLoading) return;
    const payload = getVariantPayload(VariantFlags.PricingExperiment) as { price?: number } | undefined;
    if (payload?.price === undefined) return;
    hasSetPriceRef.current = true;
    setPriceVariantProperty(String(payload.price));
  }, [isFlagsLoading, getVariantPayload, setPriceVariantProperty]);

  const {
    coupon,
    setCoupon,
    clearInviteCode,
    couponValidationError,
    isSubmitting,
    getPrice,
    handleSubmit,
    handleExpressCheckout,
    isTrial,
    isPriceLoading,
    priceRecurring,
    priceSubtotal,
    priceDiscount,
    priceTax,
    priceTotal,
    email: sessionEmail,
    form,
  } = useCollectPaymentMethod();

  // `?force-new-email=1` is set by the "already a member" interstitial when
  // the user taps "Use a different email to sign up". On that path we know
  // the visitor wants to buy a membership for someone else, so we must NOT
  // let Express Checkout pull the wallet email — they'd loop right back to
  // the same VIP account. Hide the payment methods until they type a new
  // valid email into the form. Once the form has a valid email, both the
  // wallet button and the manual card form re-mount.
  //
  // This adds friction ONLY for this rare loop case. The common path
  // (no flag) keeps the existing email-or-wallet fallback intact — no
  // extra clicks for normal Google Pay / Apple Pay users.
  const forceNewEmail = searchParams.get('force-new-email') === '1';
  const watchedEmail = form.watch('email');
  const hasValidEmail = !!watchedEmail && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(watchedEmail);
  const paymentMethodsLocked = forceNewEmail && !hasValidEmail;

  // Refetch the price (for tax) once the zip is a complete 5-digit value.
  const { isDebouncePending } = useZipPriceRefetch(form.watch('zip'), getPrice);

  // True from the moment a valid zip is entered until the new (taxed) price
  // resolves — covers both the debounce wait and the network request. During
  // this window the displayed tax/total are stale, so the pricing CTA shows a
  // "Calculating…" state instead of flashing $0 tax / an untaxed "Total".
  const hasValidZip = /^\d{5}(-\d{4})?$/.test(form.watch('zip') ?? '');
  const isCalculatingTax = hasValidZip && (isDebouncePending || isPriceLoading);

  // Ensure a funnel session exists before either prefill or blur capture
  // can run. Users entering at /payment directly (no /join upstream) get a
  // session minted here; users who came through /join already have one and
  // we reuse it.
  useEnsureFunnelSession();

  // Funnel lead is the single source of truth for email + name. Hydrates
  // from GET /v1/intake/session/me/lead once the session exists.
  const { lead: funnelLead, updateLead } = useFunnelLead();
  useEffect(() => {
    if (!funnelLead) return;
    const composedName = [funnelLead.firstName, funnelLead.lastName].filter(Boolean).join(' ').trim();
    if (composedName && !form.getValues('name')) {
      form.setValue('name', composedName);
    }
    // On the force-new-email path the user explicitly wants to sign up someone
    // else, so do NOT hydrate the prior member's email — that would unlock the
    // wallet against the same email and loop back to the already-member screen.
    if (!forceNewEmail && funnelLead.email && !form.getValues('email')) {
      form.setValue('email', funnelLead.email);
    }
  }, [funnelLead, form, forceNewEmail]);

  // Capture email blur into the funnel lead. Same code path as JoinStep1's
  // email field — direct-to-/payment users land in `funnel_leads` just like
  // /join users do.
  const handleLeadCapture = useCallback(
    async (email: string) => {
      try {
        await updateLead({ email });
      } catch (error) {
        console.error('Failed to capture funnel email:', error);
      }
    },
    [updateLead]
  );

  // Capture name blur into the funnel lead. The payment form has a single
  // `name` field; we split on the last whitespace into first/last so the
  // funnel_leads row matches the shape collected on /join.
  const handleNameCapture = useCallback(
    async (fullName: string) => {
      const trimmed = fullName.trim();
      if (!trimmed) return;
      const lastSpace = trimmed.lastIndexOf(' ');
      const firstName = lastSpace === -1 ? trimmed : trimmed.slice(0, lastSpace).trim();
      const lastName = lastSpace === -1 ? undefined : trimmed.slice(lastSpace + 1).trim() || undefined;
      try {
        await updateLead({ firstName, lastName });
      } catch (error) {
        console.error('Failed to capture funnel name:', error);
      }
    },
    [updateLead]
  );

  // Regular annual price (without discount) - derived from API subtotal
  const regularAnnualPrice =
    typeof priceSubtotal === 'number' ? Math.round(priceSubtotal / 100) : undefined;

  // Discounted first charge (when coupon applied).
  //
  // Trial flow: `priceRecurring.amount` holds the post-discount annual
  // charge (the user pays $0 today, then this on renewal). Use it directly.
  //
  // Non-trial (direct-pay): `/membership/price` does NOT include
  // `recurring.amount`. The discount lives on `priceDiscount` (cents off
  // the subtotal). Derive the post-discount annual charge as
  // `subtotal - discount` so PricingHeader can show the coupon-applied
  // price instead of falling back to the full subtotal.
  const discountedFirstCharge =
    typeof priceRecurring?.amount === 'number'
      ? Math.round(priceRecurring.amount / 100)
      : typeof priceSubtotal === 'number' &&
          typeof priceDiscount === 'number' &&
          priceDiscount > 0
        ? Math.round((priceSubtotal - priceDiscount) / 100)
        : undefined;

  // Discounted recurring price in cents — what the pricing CTA's Membership
  // line strikes-through the subtotal with. For trials the backend provides
  // `priceRecurring.amount`; for direct-pay it doesn't, so derive from
  // subtotal - discount (cents).
  const recurringCents =
    typeof priceRecurring?.amount === 'number'
      ? priceRecurring.amount
      : typeof priceSubtotal === 'number' && typeof priceDiscount === 'number' && priceDiscount > 0
        ? priceSubtotal - priceDiscount
        : priceSubtotal;

  // Determine if a coupon discount is active
  const isCouponApplied =
    discountedFirstCharge !== undefined &&
    regularAnnualPrice !== undefined &&
    discountedFirstCharge < regularAnnualPrice;

  const discountPercent =
    isCouponApplied && regularAnnualPrice
      ? Math.round(((regularAnnualPrice - discountedFirstCharge) / regularAnnualPrice) * 100)
      : 0;

  // Total to display. Only show the taxed `priceTotal` when the zip is a valid
  // 5 digits; otherwise (no zip, or the user edited it back below 5 digits)
  // show the untaxed total so we don't keep displaying a stale taxed total
  // (e.g. editing 78212 -> 7821 must drop the tax, not leave $212.13). The
  // untaxed total is subtotal - discount, falling back to subtotal.
  const untaxedTotalCents =
    typeof priceSubtotal === 'number'
      ? priceSubtotal - (typeof priceDiscount === 'number' ? priceDiscount : 0)
      : undefined;
  const displayTotalCents = hasValidZip ? priceTotal : untaxedTotalCents;

  // Coupon input expand/collapse state
  const [isCouponExpanded, setIsCouponExpanded] = useState(false);

  // Handle removing an applied coupon. The discount may come from either a
  // user-entered promo code OR a persisted invite code (e.g. VIP_TEST in
  // localStorage); clear both so the price re-fetch returns the regular price.
  const handleRemoveCoupon = useCallback(() => {
    clearInviteCode();
    setCoupon('');
    setIsCouponExpanded(false);
    // Pass empty coupon explicitly to avoid stale closure reading old tempCoupon
    getPrice('');
  }, [clearInviteCode, setCoupon, getPrice]);

  // Check if form is valid for sticky CTA
  const isFormValid = form.formState.isValid;

  // Focus + scroll the zip input into view. Wired up to the "Enter Zipcode"
  // placeholder in the pricing breakdown so users can jump straight to it.
  const handleClickEnterZipcode = useCallback(() => {
    const el = document.querySelector('input[name="zip"]');
    if (el instanceof HTMLInputElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Defer focus until the smooth scroll has had a chance to settle,
      // otherwise some browsers re-scroll the input out of view.
      window.setTimeout(() => el.focus(), 350);
    }
  }, []);

  return (
    <PaymentPageLayout
      header={<Header />}
      hero={
        !isTrial && (
          <h1 className="text-[28px] font-bold leading-[1.15] tracking-tight text-catch-dark-blue-90 lg:mx-auto lg:max-w-[460px] lg:text-center lg:text-[34px]">
            A small price to pay for ongoing peace of&nbsp;mind
          </h1>
        )
      }
      paymentCard={
        <div className="flex flex-col gap-3">
          <PaymentCard id="payment-card" isSticky={false}>
          {/* Pricing */}
          <PricingHeader
            isTrial={isTrial}
            annualPrice={regularAnnualPrice}
            isLoading={isPriceLoading}
            couponCode={isCouponApplied ? coupon : undefined}
            discountedAnnualPrice={isCouponApplied ? discountedFirstCharge : undefined}
            onRemoveCoupon={handleRemoveCoupon}
          />

          {/* When the visitor was bounced here from the "already a member"
              interstitial, lock all payment methods until they type a fresh
              email. Otherwise Google Pay would silently refill the wallet's
              email and loop them right back. The notice points at the
              email field below to guide them. */}
          {paymentMethodsLocked && (
            <div
              data-testid="force-new-email-notice"
              role="status"
              className="rounded-2xl border-1 border-solid border-catch-light-blue bg-white p-[16px_20px] text-sm text-catch-dark-blue-90"
            >
              <strong className="font-semibold">Enter a new email below</strong> to sign up someone else for Catch. The
              payment options will appear once you do.
            </div>
          )}

          {/* Express Checkout - wallets first as primary option */}
          {!paymentMethodsLocked && (
            <ExpressCheckoutSection onConfirm={handleExpressCheckout} />
          )}

          {/* Form Fields - manual card entry */}
          <form id="payment-checkout-form" onSubmit={form.handleSubmit(() => handleSubmit())}>
            <div className="flex flex-col gap-4">
              <EmailField
                registration={form.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={form.formState.errors.email?.message}
                onLeadCapture={handleLeadCapture}
              />

              <NameField
                registration={form.register('name', {
                  required: 'Name is required',
                })}
                error={form.formState.errors.name?.message}
                onNameCapture={handleNameCapture}
              />

              {/* When force-new-email is active and we haven't seen a valid
                  email yet, hide everything past EmailField + NameField.
                  Re-renders cleanly once the email validates. */}
              {!paymentMethodsLocked && (
                <>
                  <CardSection
                    email={form.watch('email')}
                    name={form.watch('name')}
                  />

                  <ZipCodeField
                    registration={form.register('zip', {
                      required: 'Zip code is required',
                      pattern: {
                        value: /^\d{5}$/,
                        message: 'Enter a valid 5-digit zip code',
                      },
                    })}
                    error={form.formState.errors.zip?.message}
                    isCalculating={isCalculatingTax}
                  />

                  <CouponInput
                    value={coupon}
                    onChange={setCoupon}
                    onApply={() => getPrice()}
                    error={couponValidationError || undefined}
                    isExpanded={isCouponExpanded || !!coupon}
                    onExpand={() => setIsCouponExpanded(true)}
                    isApplied={isCouponApplied}
                    isValidating={isPriceLoading}
                    couponCode={coupon}
                    discountPercent={discountPercent}
                    onRemoveCoupon={handleRemoveCoupon}
                  />
                </>
              )}
            </div>

            {/* Trial charge notice + legal disclaimer. No in-card submit
                button: on mobile the always-visible sticky CTA submits the
                form; on desktop the separate inline pricing-CTA card does.
                Both target this form via formSubmitId. */}
            {!paymentMethodsLocked && (
              <div className="mt-6 flex flex-col gap-3">
                {isTrial && (
                  <TrialChargeNotice
                    annualPrice={regularAnnualPrice}
                    discountedFirstCharge={discountedFirstCharge}
                  />
                )}
                <LegalDisclaimer />
              </div>
            )}
          </form>

          {/* Trust badges (SSL / HIPAA) */}
          <PaymentTrustBadges />
          </PaymentCard>

          {/* Desktop-only inline pricing CTA — separate card below the main
              payment card. Mirrors the prototype's `hidden lg:block` desktop
              treatment. Submits the main form via formSubmitId. Mobile users
              get the sticky CTA at the viewport bottom instead. */}
          {!paymentMethodsLocked && (
            <div className="mt-4 hidden md:block">
              <PaymentCard isSticky={false}>
                <StickyPricingCTA
                  variant="inline"
                  formSubmitId="payment-checkout-form"
                  subtotalCents={priceSubtotal}
                  recurringCents={recurringCents}
                  taxCents={hasValidZip ? priceTax : undefined}
                  totalCents={displayTotalCents}
                  isCalculatingTax={isCalculatingTax}
                  isCouponApplied={isCouponApplied}
                  discountPercent={discountPercent}
                  isEnabled={isFormValid}
                  isSubmitting={isSubmitting}
                  onClick={() => undefined}
                  onClickEnterZipcode={handleClickEnterZipcode}
                  submitLabel={isTrial ? 'Start my risk assessment' : 'Complete payment'}
                />
              </PaymentCard>
            </div>
          )}
        </div>
      }
      testimonials={<TestimonialsSection />}
      privacy={<PrivacySection />}
      faqs={
        <div id="faqs">
          <FAQSection faqSetSlug={isMultiDiseaseCohort ? 'payment-multi-disease' : 'payment-cancer'} />
        </div>
      }
      contactButton={<ContactButton />}
      footer={
        <div className="flex flex-col gap-6">
          <FooterLinks />
          <div className="flex flex-col gap-4">
            <HsaFsaDisclaimer />
            <FooterDisclaimer />
          </div>
        </div>
      }
      stickyMobileCTA={
        // Suppress the sticky CTA while we're blocking checkout for the
        // force-new-email loop. Otherwise it floats over a form that
        // intentionally hides its submit button.
        <StickyPricingCTA
          formSubmitId="payment-checkout-form"
          subtotalCents={priceSubtotal}
          recurringCents={recurringCents}
          // `priceTax` flips from undefined -> a cent value once the backend
          // accepts the zip and returns a tax estimate. Gate the breakdown's
          // tax line + header label switch on a syntactically-valid zip too,
          // so partial input doesn't briefly flash "Total due today" with a
          // stale tax value from a previous lookup.
          taxCents={hasValidZip ? priceTax : undefined}
          totalCents={displayTotalCents}
          isCalculatingTax={isCalculatingTax}
          isCouponApplied={isCouponApplied}
          discountPercent={discountPercent}
          isEnabled={isFormValid}
          isSubmitting={isSubmitting}
          onClick={() => undefined}
          onClickEnterZipcode={handleClickEnterZipcode}
          // Always visible on mobile (matches the prototype's persistent
          // sticky bar). Only suppressed while checkout is blocked.
          isVisible={!paymentMethodsLocked}
          submitLabel={isTrial ? 'Start my risk assessment' : 'Complete payment'}
        />
      }
    />
  );
}

/**
 * Wrapper that provides Stripe Elements context
 */
function PaymentPageWithElements({ children }: { children: React.ReactNode }) {
  const { amount } = useStripeAmount();
  const { isTrial } = usePurchaseModel();

  const { stripePromise, stripeOptions } = usePaymentController({
    variant: 'seamless',
    amount: amount ?? 0,
    isTrial,
  });

  const stripeStatus = useStripeLoadStatus(stripePromise);

  if (stripeStatus === 'failed') {
    return <StripeLoadError />;
  }

  return (
    <Elements options={stripeOptions} stripe={stripePromise}>
      {children}
    </Elements>
  );
}

/**
 * Main exported component
 */
export function PaymentPageV2() {
  const setIntercomIsVisible = useSetIntercomIsVisible();

  useOnMount(() => {
    window.scrollTo(0, 0);
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <StripeAmountProvider>
      <PaymentPageWithElements>
        <PaymentForm />
      </PaymentPageWithElements>
    </StripeAmountProvider>
  );
}

export default PaymentPageV2;
